// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  CheckCircle2, CloudUpload, Loader2, AlertTriangle, Rocket,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getBuilderStatusBatch,
  provisionClientOnBuilder,
  BuilderStatus,
} from '../services/api';

/**
 * Builder workspace provisioning card shown on the client's Settings → General
 * page. Surfaces whether the client's builder workspace is provisioned and
 * lets the founder/admin provision it on demand. Aligns with the rule that
 * the business (not the consultant) owns the builder workspace.
 */
const BuilderWorkspaceCard: React.FC = () => {
  const clientId = typeof window !== 'undefined' ? localStorage.getItem('client_id') : null;
  const clientEmail = typeof window !== 'undefined' ? localStorage.getItem('client_email') || localStorage.getItem('user_email') : null;

  const [status, setStatus] = useState<BuilderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);

  const load = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const res = await getBuilderStatusBatch([clientId]);
      setStatus(res.statuses[clientId] || null);
    } catch (err) {
      console.error('[BuilderWorkspaceCard] load failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [clientId]);

  const handleProvision = async () => {
    if (!clientId) return;
    setProvisioning(true);
    try {
      await provisionClientOnBuilder(clientId);
      toast.success('Builder workspace provisioned');
      await load();
    } catch (err: any) {
      console.error('[BuilderWorkspaceCard] provision failed', err);
      const detail = err?.response?.data?.detail || err?.message || 'Failed to provision builder workspace';
      toast.error(detail);
    } finally {
      setProvisioning(false);
    }
  };

  if (!clientId) return null;

  const billingMismatch =
    status?.provisioned &&
    status.billingEmail &&
    clientEmail &&
    status.billingEmail.toLowerCase() !== clientEmail.toLowerCase();

  return (
    <div className="bg-white rounded-card border border-surface-border shadow-card">
      <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
        <Rocket size={16} className="text-brand-500" />
        <h2 className="text-sm font-semibold text-gray-900">Builder workspace</h2>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 size={16} className="animate-spin" /> Checking status…
          </div>
        ) : status?.provisioned ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-green-700">
              <CheckCircle2 size={16} /> Workspace is provisioned
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                <div className="text-gray-400 uppercase tracking-wider font-medium mb-0.5">Projects</div>
                <div className="text-gray-900 font-medium">{status.projectCount ?? 0}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                <div className="text-gray-400 uppercase tracking-wider font-medium mb-0.5">Team members</div>
                <div className="text-gray-900 font-medium">{status.memberCount ?? 0}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                <div className="text-gray-400 uppercase tracking-wider font-medium mb-0.5">Billing anchor</div>
                <div className="text-gray-900 font-medium truncate" title={status.billingEmail || '—'}>
                  {status.billingEmail || '—'}
                </div>
              </div>
            </div>
            {billingMismatch && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Billing anchor doesn't match this workspace</div>
                  <div className="mt-0.5 text-amber-700">
                    Currently <span className="font-sans">{status.billingEmail}</span> — expected <span className="font-sans">{clientEmail}</span>.
                    Ask an admin to rebind on the builder <code>/admin/projects</code> page, or re-provision below.
                  </div>
                </div>
              </div>
            )}
            <div className="pt-1">
              <button
                onClick={handleProvision}
                disabled={provisioning}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 whitespace-nowrap"
                title="Re-sync workspace (updates members, does not rotate the API key)"
              >
                {provisioning ? <Loader2 size={12} className="animate-spin" /> : <CloudUpload size={12} />}
                Re-sync
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-medium">Not provisioned yet</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Your builder workspace is where AI agents run and projects live. Provisioning creates it with <span className="font-sans">{clientEmail || 'your account email'}</span> as the workspace founder. Team members you invite show up there too.
                </div>
              </div>
            </div>
            <button
              onClick={handleProvision}
              disabled={provisioning}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {provisioning ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Provisioning…
                </>
              ) : (
                <>
                  <Rocket size={14} /> Set up builder workspace
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuilderWorkspaceCard;
